import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import axios from 'axios';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import { AppCard, AppInput, AppModal } from '@/layout/AppComponents';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import AppLink from '../../../../../routing/helpers/AppLink';
import CommunicationPipelineLogin from '../CommunicationPipelineLogin';
import AppSubmitButton from '../../../../components/form/AppSubmitButton';
import errorHandler from '../../../../components/error/ErrorHandler';

export default function CommunicationPipelineHomeUser() {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [showRecoverPasswordModal, setShowRecoverPasswordModal] =
    useState(false);
  const reportTemplate = useSelector(
    (state) => state.currentPage?.moduleData?.reportTemplate
  );
  const [validationSchema] = useState(
    Yup.object().shape({
      reportHex: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-userManagement" name="required" />)
      ),
    })
  );

  const handleAuth = useCallback(
    (payload) => {
      navigate(
        path({
          name: payload?.routeName,
          params: payload?.routeParams,
        })
      );
    },
    [moduleBridgeHex]
  );

  const handleRecoverPassword = useCallback(
    async (payload) => {
      axios
        .post(
          axiosPath(
            'api_r_user_module_communicationPipeline_user_report_recover_password'
          ),
          {
            ...payload,
            moduleBridgeHex,
          }
        )
        .then(() => {})
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowRecoverPasswordModal(false));
    },
    [moduleBridgeHex]
  );

  return (
    <AppCard className="p-5">
      <div className="container">
        <div className="mx-auto mb-5 text-center">
          {auth.user?.client?.logoUrl && (
            <img
              src={auth.user?.client?.logoUrl}
              className="img-fluid"
              alt={auth.user?.client?.name}
              title={auth.user?.client?.name}
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
              }}
            />
          )}
        </div>
        <div className="mx-auto">
          <div className="row">
            <div className="offset-2" />
            <div className="col-3 border-right">
              <div className="mb-3">
                <h6>
                  <strong>
                    <Translator
                      file="module-communicationPipeline"
                      name={
                        reportTemplate === 'haizea'
                          ? 'communication'
                          : 'complaints'
                      }
                    />
                  </strong>
                </h6>
                <AppLink
                  path="user_module_communicationPipeline_user_report_add"
                  params={{
                    moduleBridgeHex,
                  }}
                  className="btn btn-md btn-primary btn-block mt-1"
                >
                  <Translator
                    file="module-communicationPipeline"
                    name={
                      reportTemplate === 'haizea'
                        ? 'new-communication'
                        : 'new-complaint'
                    }
                  />
                </AppLink>
              </div>
              <div className="mb-3">
                <h6>
                  <strong>
                    <Translator
                      file="module-communicationPipeline"
                      name="query"
                    />
                  </strong>
                </h6>
                <AppLink
                  path="user_module_communicationPipeline_user_inquiry_add"
                  params={{
                    moduleBridgeHex,
                  }}
                  className="btn btn-md btn-primary btn-block mt-1"
                >
                  <Translator
                    file="module-communicationPipeline"
                    name="new-query"
                  />
                </AppLink>
              </div>
            </div>
            <div className="col-5">
              <CommunicationPipelineLogin onAuth={handleAuth} />
              <span className="pointer">
                <small onClick={() => setShowRecoverPasswordModal(true)}>
                  <Translator
                    file="module-communicationPipeline"
                    name="forgotten-password"
                  />
                </small>
              </span>
            </div>
          </div>
        </div>
        <AppModal
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-communicationPipeline"
              name="password-recovery"
            />
          )}
          show={showRecoverPasswordModal}
          onClose={() => setShowRecoverPasswordModal(false)}
        >
          <Formik
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={{
              reportHex: '',
            }}
            onSubmit={handleRecoverPassword}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <Translator
                  file="module-communicationPipeline"
                  name={
                    reportTemplate === 'haizea'
                      ? 'password-sent-email-address-registered-password-recovered-haizea'
                      : 'password-sent-email-address-registered-password-recovered'
                  }
                />
                <AppInput
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-communicationPipeline"
                      name={
                        reportTemplate === 'haizea'
                          ? 'communication-code'
                          : 'complaint-code'
                      }
                    />
                  )}
                  value={values.reportHex}
                  error={errors?.reportHex}
                  onChange={(v) => setFieldValue('reportHex', v)}
                  isClearable
                />
                <AppSubmitButton
                  className="btn btn-md btn-primary btn-block mt-2"
                  isSubmitting={isSubmitting}
                >
                  <Translator
                    file="module-communicationPipeline"
                    name="send-to"
                  />
                </AppSubmitButton>
              </form>
            )}
          </Formik>
        </AppModal>
      </div>
    </AppCard>
  );
}
